/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Pressable, TouchableOpacity, View } from 'react-native';
import { Portal } from '@gorhom/portal';

import IconView from '@airasia-phoenix/react-native/components/IconView';
import StickyBottomBarView from '@airasia-phoenix/react-native/components/StickyBottomBarView';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import { DatePicker } from '@airasia-phoenix/react-native/widgets';
import type {
  DatePickerWithBottomSheetProps,
  DatePickerWithBottomSheetRefs,
} from '@airasia-phoenix/react-native/widgets';
import { StyleSheet } from 'react-native-unistyles';
import Close from '@move-travel/react-native-phoenix-icons/Close';

const MobileWebDatePickerBottomSheet = forwardRef<
  DatePickerWithBottomSheetRefs,
  DatePickerWithBottomSheetProps
>(
  (
    { bottomSheetViewProps, datePickerProps, stickyBottomBarViewProps },
    ref,
  ) => {
    const [visible, setVisible] = useState(false);
    const isVisibleRef = useRef(false);

    const handleClose = useCallback(() => {
      if (!isVisibleRef.current) return;

      isVisibleRef.current = false;
      setVisible(false);
      bottomSheetViewProps?.onChange?.(-1);
      bottomSheetViewProps?.onBottomSheetClose?.();
    }, [bottomSheetViewProps]);

    const handleOpen = useCallback(() => {
      if (isVisibleRef.current) return;

      isVisibleRef.current = true;
      setVisible(true);
      bottomSheetViewProps?.onChange?.(0);
    }, [bottomSheetViewProps]);

    useImperativeHandle(ref, () => ({
      toggleDatePicker() {
        if (isVisibleRef.current) {
          handleClose();
          return;
        }

        handleOpen();
      },
    }));

    if (!visible) return null;

    return (
      <Portal hostName="flights-web-portal-host">
        <View
          style={styles.container}
          testID="mobile-web-date-picker-bottom-sheet"
        >
          <Pressable
            style={[
              styles.backdrop,
              {
                backgroundColor:
                  bottomSheetViewProps?.backdropProps?.backgroundColor ??
                  pd.sys.colour.overlay.toString(),
              },
            ]}
            testID="mobile-web-date-picker-backdrop"
          />
          <View
            style={[
              styles.sheet,
              { height: bottomSheetViewProps?.height ?? '100%' },
            ]}
          >
            <View style={styles.content}>
              <View style={styles.header}>
                <TouchableOpacity
                  style={styles.closeIcon}
                  onPress={handleClose}
                  testID="mobile-web-date-picker-close"
                >
                  <IconView
                    height={24}
                    width={24}
                    tint={getColor(pd.comp.bottom_sheet.icon.colour)}
                  >
                    <Close />
                  </IconView>
                </TouchableOpacity>
              </View>
              <View style={styles.content}>
                <DatePicker isInsideBottomSheetView {...datePickerProps} />
              </View>
              <StickyBottomBarView {...stickyBottomBarViewProps} />
            </View>
          </View>
        </View>
      </Portal>
    );
  },
);

MobileWebDatePickerBottomSheet.displayName = 'MobileWebDatePickerBottomSheet';

export default MobileWebDatePickerBottomSheet;

const styles = StyleSheet.create((pd) => ({
  container: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1000,
  },
  backdrop: {
    flex: 1,
  },
  sheet: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    backgroundColor: getColor(pd.comp.bottom_sheet.container.colour),
    borderTopLeftRadius: {
      xs: 0,
      md: 14,
    },
    borderTopRightRadius: {
      xs: 0,
      md: 14,
    },
  },
  header: {
    flexDirection: 'row',
    marginTop: pd.ref.space.spacing40.px,
    zIndex: 1,
    top: 15,
    left: 20,
  },
  closeIcon: {
    marginRight: pd.ref.space.spacing20.px,
  },
  content: {
    flex: 1,
    minHeight: 0,
  },
}));
